import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import deepEqual from 'deep-equal';
import { ThemeProvider } from '@mui/material/styles';
import { useQuery } from 'utils/hooks/useQuery';
import { debounce, debounceAsync } from 'utils/misc/debounce';
import { theme } from '../../../components/layouts/theme';
import { UserContext } from 'context/User/UserContext';
import { deepCloneObject } from 'store/utility';
import {
	addMessage,
	clearMessages,
	toggleDrawer,
} from 'store/ancillary/actions';
import {
	raiseErrorBanner,
	raiseWarningBanner,
} from 'components/MessageDispatch/messageDispatch';
import { Button } from '@upmc/ims.component-library';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Snackbar from '@mui/material/Snackbar';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import Tooltip from '@mui/material/Tooltip';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import Typography from '@mui/material/Typography';
import { ValidationTable } from './components/ValidationTable';
import { ProgressBar } from './components/ProgressBar';
import { SideDrawer } from './components/SideDrawer';
import { JobCategoryDirectory } from './components/JobCategoryDirectory';
import { ProxyDialog } from './components/ProxyDialog';
import { DiscardDialog } from './components/DiscardDialog';
import { Supplement } from './components/Supplements/Supplement';
import { HomeButton } from 'components/UI/Input/Button';
import PageWithBreadcrumbs from 'layouts/PageWithBreadcrumbs';
import {
	CLASS_SPEED_DIAL,
	DirectReport,
	JobCategory,
	Supplement as SupplementClass,
	LINK_FAQ,
	OBJ_PARAMS,
	OBJ_APP_BRIDGES,
	OBJ_APP_HP,
} from './_constants';
import { NOTIFICATION_TYPES } from 'constants/notifications';
import {
	getReportAssignments,
	getUnmappedReports,
	nanoid,
	syncSnapshot,
} from './_utils';
import {
	getDirectReport,
	getDirectReports,
	getJobCategories,
	getProxies,
	getProxy,
	getSupplements,
	postSubmit,
} from './_api';

const ManagerValidation = () => {
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo?.impersonationProfile ??
		contextUser.userInfo.userProfile;

	/**
	 * Optional unique identifier of report when performing individual validations.
	 * @type {number=}
	 */
	const reportID = useQuery().get(OBJ_PARAMS.ID) ?? null;

	const dispatch = useDispatch();
	const rNavDrawerOpen = useSelector(state => state?.ancillary?.drawerOpen);
	const rError = useSelector(state =>
		state?.ancillary?.messages?.length > 0 &&
		state.ancillary.messages[0]?.['type'] === NOTIFICATION_TYPES.ERROR
			? state.ancillary.messages[0].text
			: null,
	);

	/**
	 * Array of staff assignments. Filtered to staffer assignments when sSelectedID non-null.
	 * @type [DirectReport[], Function]
	 */
	const [sArrReports, setArrReports] = useState([]);
	/**
	 * Array of staff assignments prior to edit. Enables edit discards.
	 * @type [DirectReport[], Function]
	 */
	const [sArrSnapshot, setArrSnapshot] = useState([]);
	/**
	 * Array of supplements validly inputted during edit.
	 * @type [SupplementClass[], Function]
	 */
	const [sArrSupplements, setArrSupplements] = useState([]);
	const [sClickedJobCategory, setClickedJobCategory] = useState(null);
	const [sIsDrawerOpen, setDrawerOpen] = useState(true);
	const [sIsSpeedDialOpen, setIsSpeedDialOpen] = useState(false);
	/**
	 * Undefined value used to indicate initial load has not occurred. True value used to indicate presence of unmapped report(s).
	 */
	const [sHasUnmapped, setHasUnmapped] = useState();
	const [sIsDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
	const [sIsProxyDialogOpen, setIsProxyDialogOpen] = useState(false);
	const [sJobCategoryDirectoryKey, setJobCategoryDirectoryKey] = useState(
		Date.now(),
	);
	const [sProxies, setProxies] = useState(null);
	/**
	 * Base staffer selected for editing.
	 * @type [DirectReport, Function]
	 */
	const [sSelectedReport, setSelectedReport] = useState(null);
	const [sSelectedProxy, setSelectedProxy] = useState(null);
	const [sSnackbarOpen, setSnackbarOpen] = useState(false);
	const [sSystemID, setSystemID] = useState(OBJ_APP_BRIDGES.ID);

	//#region Individual validation local state
	const [sIsLoading, setIsLoading] = useState(false);
	const [sIsRestricted, setIsRestricted] = useState(false);
	const [sTrackingNumber, setTrackingNumber] = useState();
	//#endregion

	/**
	 * Memoized asynchronous function that retrieves a user's direct reports, including those automatically assigned by proxy.
	 * @returns {Promise<DirectReport[]>} array of direct reports user is responsible for.
	 */
	const getAllReports = useCallback(
		async (user, systemID) => {
			let arrReports = [];
			try {
				arrReports = await getDirectReports(user.ID, systemID);

				// handle exempt users whose validation responsibilities are delegated
				if (user.Email) {
					const objProxy = await getProxy(user.Email);
					if (objProxy?.exceptionID) {
						if (user.ID === objProxy.exceptionID) {
							arrReports = [];
						} else {
							arrReports = [
								...arrReports,
								...(await getDirectReports(
									objProxy.exceptionID,
									systemID,
								)),
							];
						}
					}
				}
			} catch (ex) {
				dispatch(clearMessages());
				raiseErrorBanner(ex, dispatch);
				setIsLoading(false);
			} finally {
				return arrReports;
			}
		},
		[dispatch],
	);

	const getReport = useCallback(
		async (user, reportID, systemID) => {
			let arrReports = [];
			try {
				const response = await getDirectReport(
					user.ID,
					reportID,
					systemID,
					false,
					dispatch,
				);
				arrReports = response?.data?.payload
					? response.data.payload.map(
							report => new DirectReport(report),
						)
					: [];
				const isRestricted = response?.data?.messages?.length > 0;
				setIsRestricted(isRestricted);
				if (isRestricted) {
					dispatch(clearMessages());
					response.data.messages.forEach(message =>
						raiseWarningBanner(message.Value, dispatch),
					);
				}
			} catch (ex) {
				dispatch(clearMessages());
				raiseErrorBanner(ex, dispatch);
				setIsLoading(false);
			} finally {
				return arrReports;
			}
		},
		[dispatch],
	);

	/**
	 * Memoized function that writes to local state a prepared set of direct report(s) possessing assignments and also the conditional subset not possessing assignments.
	 * @returns {DirectReport[]} prepared set deep copy.
	 */
	const processReports = useCallback(arrReports => {
		setHasUnmapped(getUnmappedReports(arrReports)?.length > 0);

		// ensure unique identifier assigned even if assignment not yet existent
		arrReports = arrReports.map(report => {
			if (!report.assignment?.id || report.assignment.id === '-1') {
				report.assignment.id = nanoid();
			}
			return report;
		});
		setArrReports(arrReports);
		return cloneDeep(arrReports);
	}, []);

	const hydrateJobCategoryActivity = useCallback(async arrReports => {
		const arrIds = [
			...new Set(
				arrReports
					.map(report => report.assignment?.jobCategory?.id)
					.filter(id => id > 0),
			),
		];
		if (arrIds.length < 1) {
			return arrReports;
		}

		const arrDetails = await getJobCategories(arrIds);
		const mapActivity = new Map(arrDetails.map(jc => [jc.id, jc.active]));

		return arrReports.map(report => {
			const idJC = report.assignment?.jobCategory?.id;
			if (idJC > 0 && mapActivity.has(idJC)) {
				report.assignment.jobCategory.active = mapActivity.get(idJC);
			}
			return report;
		});
	}, []);

	useEffect(() => {
		let ignore = false;

		/**
		 * Asynchronously loads all data for end-user, impersonation-inclusive.
		 *
		 * @param {import('context/User/UserContext').User} user end-user.
		 */
		const render = async user => {
			dispatch(clearMessages());
			setIsLoading(true);
			setArrReports([]);
			setArrSupplements([]);
			setHasUnmapped(undefined);
			
			let arrReports = await getAllReports(user, OBJ_APP_BRIDGES.ID);
			
			if (!ignore) {
				processReports(arrReports);
				
				// ensure proxy data is reset if user changes
				setSystemID(OBJ_APP_BRIDGES.ID);
				setProxies(null);
				setSelectedProxy(null);
				setIsLoading(false);
				dispatch(toggleDrawer(false));
			}
		};

		if (user?.ID && !reportID) {
			render(user);
		}

		return () => {
			ignore = true;
			dispatch(toggleDrawer(true));
		};
	}, [user, reportID, dispatch, getAllReports, processReports]);

	useEffect(() => {
		let ignore = false;

		/**
		 * Asynchronously loads data of staffer.
		 *
		 * @param {import('context/User/UserContext').User} user end-user.
		 * @param {number} reportID unique identifier of staffer for individual validations.
		 */
		const render = async (user, reportID = -1) => {
			dispatch(clearMessages());
			setIsLoading(true);
			setArrReports([]);
			setArrSnapshot([]);
			setArrSupplements([]);
			setHasUnmapped(undefined);

			let arrReports = await getReport(
				user,
				reportID,
				OBJ_APP_BRIDGES.ID,
			);
			if (!ignore) {
				arrReports = await hydrateJobCategoryActivity(arrReports);
				setArrSnapshot(processReports(arrReports));

				setTrackingNumber(undefined);
				setIsLoading(false);
				dispatch(toggleDrawer(false));
			}
		};

		if (user?.ID && reportID) {
			render(user, reportID);
		}

		return () => {
			ignore = true;
			dispatch(toggleDrawer(true));
		};
	}, [
		user,
		reportID,
		dispatch,
		getReport,
		processReports,
		hydrateJobCategoryActivity,
	]);

	const onCreateHandler = useCallback(id => {
		setArrReports(arrReports => {
			let arrReportsCopy = [...arrReports];
			const index = arrReportsCopy.findIndex(
				report => report.assignment.id === id,
			);
			if (index > -1) {
				const report = arrReportsCopy[index];

				let reportClone = new DirectReport({
					ID: report.id,
					Name: report.name,
					Assignment: {
						ID: '-1',
					},
				});
				reportClone.assignment.id = nanoid();

				reportClone.assignment.jobCategory = new JobCategory({});
				// add copy
				arrReportsCopy.splice(index + 1, 0, reportClone);
			}
			return arrReportsCopy;
		});
	}, []);

	const onDeleteHandler = useCallback(
		id =>
			setArrReports(arrReports => {
				let arrReportsCopy = [...arrReports];
				const index = arrReportsCopy.findIndex(
					r => r.assignment.id === id,
				);
				arrReportsCopy.splice(index, 1);
				return arrReportsCopy;
			}),
		[],
	);

	const onDialogOpenHandler = useCallback(async () => {
		if (user?.ID && sProxies === null) {
			try {
				setProxies(await getProxies(user.ID, 2));
			} catch (ex) {
				dispatch(clearMessages());
				raiseErrorBanner(ex, dispatch);
				setIsProxyDialogOpen(false);
			}
		}
	}, [user?.ID, sProxies, dispatch]);

	const onEditHandler = useCallback(
		async (id, name) => {
			window.scrollTo(0, 0);
			dispatch(clearMessages());
			setIsLoading(true);
			// preserve local state before update that occurs during processing of response payload
			const arrReportsCopy = sArrReports;

			let arrReports = await getReport(
				sSelectedProxy ?? user,
				id,
				sSystemID,
			);
			arrReports = await hydrateJobCategoryActivity(arrReports);

			processReports(arrReports);
			setSelectedReport(new DirectReport({ ID: id, Name: name }));
			// account for staffer's assignments potentially being restricted during individual load
			setArrSnapshot(syncSnapshot(arrReports, arrReportsCopy));

			setIsLoading(false);
			setDrawerOpen(true);
			setTrackingNumber(undefined);
		},
		[
			user,
			dispatch,
			sSelectedProxy,
			sArrReports,
			sSystemID,
			getReport,
			processReports,
			hydrateJobCategoryActivity,
		],
	);

	/**
	 * Memoized job category change handler that only resolves its promise if the change is valid.
	 * Validity includes uniqueness, "primary" job category prerequisite for "enhancement" job categories, and "No Access or Training Needed" job category exclusivity.
	 *
	 * @param {string} id assignment unique identifier.
	 * @param {JobCategory} jobCategory selected job category.
	 * @returns {Promise}
	 */
	const onJobCategoryHandler = useCallback((id, jobCategory) => {
		return new Promise((resolve, reject) => {
			let errorMessage = '';

			setArrReports(arrReports => {
				let arrReportsCopy = [...arrReports];
				let indexReport = arrReportsCopy.findIndex(
					report => report.assignment.id === id,
				);
				if (indexReport > -1) {
					const arrAssignments = getReportAssignments(
						arrReportsCopy[indexReport],
						arrReports,
					);
					// ensure job category not already assigned elsewhere
					if (
						arrAssignments.some(
							r =>
								r.assignment.jobCategory.id ===
									jobCategory.id && r.assignment.id !== id,
						)
					) {
						errorMessage =
							'Report is already mapped to this selection.';
					}
					// ensure "No Access No Training" is exclusive per report
					const objNoAccessNoTraining = {
						[OBJ_APP_BRIDGES.ID]: 1426,
						[OBJ_APP_HP.ID]: 100000,
					};
					const idNoAccessNoTraining =
						objNoAccessNoTraining[jobCategory.appID];

					if (!errorMessage && idNoAccessNoTraining) {
						const isSelectingNoAccessNoTraining =
							jobCategory.id === idNoAccessNoTraining;
						const hasOtherAssignedCategory = arrAssignments.some(
							r =>
								r.assignment.id !== id &&
								r.assignment.jobCategory.id > 0,
						);
						const hasNoAccessNoTrainingElsewhere =
							arrAssignments.some(
								r =>
									r.assignment.id !== id &&
									r.assignment.jobCategory.id ===
										idNoAccessNoTraining,
							);

						if (
							(isSelectingNoAccessNoTraining &&
								hasOtherAssignedCategory) ||
							hasNoAccessNoTrainingElsewhere
						) {
							errorMessage =
								'"No Access or Training Needed" cannot be assigned with another job category.';
						}
					}
					// if "enhancement", ensure presence of "primary"
					if (
						!errorMessage &&
						!jobCategory.isPrimary() &&
						!arrAssignments.some(
							r =>
								r.assignment.jobCategory.isPrimary() &&
								r.assignment.id !== id,
						)
					) {
						errorMessage =
							'An enhancement job category first requires a primary job category.';
					}
					// if alternate system or changing from one, ensure experience / wave applied or reset
					if (
						jobCategory.appID !== OBJ_APP_BRIDGES.ID ||
						(arrReportsCopy[indexReport].assignment.wave === -1 &&
							jobCategory.appID === OBJ_APP_BRIDGES.ID)
					) {
						let assignment = {
							...arrReportsCopy[indexReport].assignment,
						};
						assignment.experienced = false;
						assignment.wave =
							jobCategory.appID === OBJ_APP_BRIDGES.ID ? 3 : -1;
						arrReportsCopy[indexReport].assignment = assignment;
					}

					arrReportsCopy[indexReport].assignment.jobCategory =
						jobCategory;
				}
				return arrReportsCopy;
			});
			!errorMessage ? resolve() : reject(errorMessage);
		});
	}, []);

	const onSystemHandler = useMemo(() => {
		return debounceAsync(async (_, id) => {
			setIsLoading(true);
			dispatch(clearMessages());
			setArrSupplements([]);
			setHasUnmapped(undefined);

			const arrReports = reportID
				? await getReport(user, reportID, id)
				: await getAllReports(user, id);
			processReports(arrReports);
			setSystemID(id);

			setTrackingNumber(undefined);
			setArrSupplements([]);
			setIsLoading(false);
		});
	}, [dispatch, user, reportID, getAllReports, getReport, processReports]);

	/**
	 * Memoized assignment's experience or wave change handler.
	 * @returns {void}
	 */
	const onToggleHandler = useCallback((id, value) => {
		setArrReports(arrReports => {
			let arrReportsCopy = [...arrReports];
			let indexReport = arrReportsCopy.findIndex(
				report => report.assignment.id === id,
			);
			if (indexReport > -1) {
				if (typeof value === 'number') {
					arrReportsCopy[indexReport].assignment.wave =
						value > 0 ? value : null;
				} else {
					arrReportsCopy[indexReport].assignment.experienced =
						value !== null ? value : false;
				}
			}
			return arrReportsCopy;
		});
	}, []);

	const onProxyHandler = useCallback(
		async e => {
			const value = e?.target?.value ?? user;
			setSelectedProxy(e ? value : null);

			setIsLoading(true);
			const arrReports = reportID
				? await getReport(value, reportID, sSystemID)
				: await getAllReports(value, sSystemID);
			processReports(arrReports);

			setIsLoading(false);
			setTrackingNumber(undefined);
			setArrSupplements([]);
		},
		[user, reportID, sSystemID, getAllReports, getReport, processReports],
	);

	const onRowInfoHandler = useCallback(jobCategory => {
		setJobCategoryDirectoryKey(Date.now());
		setClickedJobCategory(jobCategory);
		setDrawerOpen(true);
	}, []);

	const onSubmitHandler = useCallback(async () => {
		setIsLoading(true);
		dispatch(clearMessages());

		const payload = sArrReports.filter(
			report => report.assignment.validated,
		);

		try {
			const trackingNumber = await postSubmit(
				user.ID,
				payload,
				sSystemID,
				sArrSupplements,
			);

			if (reportID) {
				setTrackingNumber(trackingNumber);
				dispatch(
					addMessage({
						Key: 5,
						Value: 'Success.',
					}),
				);
			} else {
				const arrReports = syncSnapshot(payload, sArrSnapshot);
				setArrReports(arrReports);
				if (sHasUnmapped) {
					setHasUnmapped(getUnmappedReports(arrReports)?.length > 0);
				}
				setSelectedReport(null);
				setSnackbarOpen(true);
			}
		} catch (ex) {
			raiseErrorBanner(ex, dispatch);
			setTrackingNumber('');
		} finally {
			setIsLoading(false);
			setArrSupplements([]);
		}
	}, [
		user,
		reportID,
		sSystemID,
		sHasUnmapped,
		sArrReports,
		sArrSnapshot,
		sArrSupplements,
		dispatch,
	]);

	const onSupplementAcceptHandler = useCallback((id, type, payload) => {
		setArrSupplements(arrSupplements => {
			let arrSupplementsCopy = [...arrSupplements];
			const index = arrSupplementsCopy.findIndex(
				supplement => supplement.id === id && supplement.type === type,
			);
			if (index > -1) {
				arrSupplementsCopy[index].value = payload;
			}
			return arrSupplementsCopy;
		});
	}, []);

	const onSupplementDiscardHandler = useCallback(id => {
		// reset validation of supplement's assignment
		setArrReports(arrReports => {
			let arrReportsCopy = [...arrReports];
			let indexReport = arrReportsCopy.findIndex(
				r => r.assignment.id === id,
			);
			arrReportsCopy[indexReport].assignment.validated =
				!arrReportsCopy[indexReport].assignment.validated;
			return arrReportsCopy;
		});
		// remove supplement(s) from tracked collection
		setArrSupplements(arrSupplements => {
			let arrSupplementsCopy = [...arrSupplements];
			arrSupplementsCopy = arrSupplementsCopy.filter(
				supplement => supplement.id !== id,
			);
			return arrSupplementsCopy;
		});
	}, []);

	const onValidateHandler = useCallback(
		async (reportID, assignmentID, assignmentJCID, assignmentValid) => {
			try {
				if (!assignmentValid) {
					const arrSupplementTypes = await getSupplements(
						assignmentJCID,
						reportID,
					);
					if (arrSupplementTypes?.length > 0 && !assignmentValid) {
						setArrSupplements(arrSupplements => {
							let arrSupplementsCopy = [...arrSupplements];
							arrSupplementTypes.forEach(type => {
								const index = arrSupplementsCopy.findIndex(
									supplement => supplement.type === type,
								);
								// copy value of existing supplement type when available
								const supplement = new SupplementClass({
									ID: assignmentID,
									Type: type,
									Value:
										index > -1
											? deepCloneObject(
													arrSupplementsCopy[index]
														.value,
												)
											: null,
								});
								arrSupplementsCopy.push(supplement);
							});
							return arrSupplementsCopy;
						});
					}
				}

				setArrReports(arrReports => {
					let arrReportsCopy = [...arrReports];
					// search by potentially stale assignment id, if assignment did not yet exist
					let indexReport = arrReportsCopy.findIndex(
						r => r.assignment.id === assignmentID,
					);
					arrReportsCopy[indexReport].assignment.validated =
						!assignmentValid;
					return arrReportsCopy;
				});
			} catch (ex) {
				raiseErrorBanner(ex, dispatch);
			}
		},
		[dispatch],
	);

	const onDiscardHandler = useMemo(() => {
		return debounce(() => {
			dispatch(clearMessages());
			setArrReports(sArrSnapshot);
			setArrSnapshot([]);
			setSelectedReport(null);
			setArrSupplements([]);
			setTrackingNumber(undefined);
			setIsDiscardDialogOpen(false);
		}, 300);
	}, [dispatch, sArrSnapshot]);

	const completionProgress = useMemo(
		() =>
			sArrReports.length > 0
				? Math.floor(
						(sArrReports.filter(
							report => report.assignment.validated,
						).length /
							sArrReports.length) *
							100,
					)
				: 0,
		[sArrReports],
	);

	const arrReportSnapshot = useMemo(() => {
		return reportID || sSelectedReport
			? sArrSnapshot.filter(
					el =>
						(reportID && el.id.toString() === reportID) ||
						(sSelectedReport && el.id === sSelectedReport.id),
				)
			: [];
	}, [reportID, sArrSnapshot, sSelectedReport]);

	/**
	 * Memoized computation of save eligibility. Considers, in order:
	 * 1. Existent user selection
	 * 2. Loading state
	 * 3. Presence of at least one validated assignment
	 * 4. A validation difference between pre-edit snapshot and validated assignment subset, whether by length or deep equality, deep equality being the last resort.
	 * @returns {boolean=false} whether a submission can occur.
	 */
	const enableSave = useMemo(() => {
		let result = arrReportSnapshot.length > 0;
		if (result) {
			result = !sIsLoading;
			if (result) {
				const arrReportValid = sArrReports.filter(
					el => el.assignment.validated,
				);
				result = arrReportValid.length > 0;
				if (result && arrReportSnapshot.length > 0) {
					const arrReportSnapshotValid = arrReportSnapshot.filter(
						el => el.assignment.validated,
					);
					result =
						arrReportSnapshotValid.length !== arrReportValid.length;
					if (!result) {
						result = arrReportSnapshotValid.some(
							(el, index) =>
								!deepEqual(el, arrReportValid[index]),
						);
					}
				}
			}
		}
		return result;
	}, [arrReportSnapshot, sIsLoading, sArrReports]);

	const onDiscardWarningHandler = useMemo(() => {
		return debounce(() => {
			if (enableSave) {
				setIsDiscardDialogOpen(true);
			} else {
				onDiscardHandler();
			}
		}, 300);
	}, [enableSave, onDiscardHandler]);

	const elBreadcrumbs = useMemo(() => {
		const currentPageStyles = {
			display: 'flex',
			alignItems: 'center',
			cursor: 'default',
		};

		const linkStyles = {
			...currentPageStyles,
			cursor: 'pointer',
		};

		const currentBC = (
			<Typography
				color='text.primary'
				sx={{ ...currentPageStyles }}
				key={'navigation-breadcrumbs--current'}
				id={'navigation-breadcrumbs--current'}
			>
				{sSelectedReport
					? sSelectedReport.name
					: 'Validation Dashboard'}
			</Typography>
		);

		return sSelectedReport
			? [
					<Link
						underline='hover'
						color='text.secondary'
						onClick={onDiscardWarningHandler}
						sx={{ ...linkStyles }}
						key={'navigation-breadcrumbs--parent'}
						id={'navigation-breadcrumbs--parent'}
					>
						Validation Dashboard
						{' (' +
							[OBJ_APP_BRIDGES, OBJ_APP_HP].filter(
								app => app.ID === sSystemID,
							)[0].Name +
							')'}
					</Link>,
					currentBC,
				]
			: [currentBC];
	}, [sSelectedReport, sSystemID, onDiscardWarningHandler]);

	const missingSupplement = useMemo(() => {
		let result;
		if (sArrSupplements.length > 0) {
			result = sArrSupplements.find(
				supplement => supplement.value === null,
			);
		}
		return result ?? null;
	}, [sArrSupplements]);

	return (
		<ThemeProvider theme={theme}>
			{!reportID && (
				<PageWithBreadcrumbs
					sx={{ marginTop: '8px', paddingBottom: '8px' }}
					breadcrumbs={elBreadcrumbs}
				/>
			)}
			{(!sSelectedReport || reportID) && !sTrackingNumber && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						marginBottom: '16px',
					}}
				>
					<ToggleButtonGroup
						color='primary'
						disabled={sIsLoading}
						exclusive
						onChange={onSystemHandler}
						size='large'
						value={sSystemID}
					>
						{[OBJ_APP_BRIDGES, OBJ_APP_HP].map((system, index) => {
							const toggle = (
								<ToggleButton
									key={system.ID}
									sx={{
										borderRadius: '8px',
										fontWeight:
											sSystemID === system.ID
												? 'bold'
												: '',
										minWidth: '145px',
									}}
									disabled={sSystemID === system.ID}
									selected={sSystemID === system.ID}
									value={system.ID}
								>
									{system.Name}
								</ToggleButton>
							);
							return system.ID !== sSystemID ? (
								<Tooltip
									title='Switch Epic Instance'
									placement={index > 0 ? 'right' : 'left'}
								>
									{toggle}
								</Tooltip>
							) : (
								toggle
							);
						})}
					</ToggleButtonGroup>
					{sSystemID !== OBJ_APP_BRIDGES.ID && (
						<Alert
							sx={{
								borderColor: 'rgba(224, 224, 224, 1)',
								borderRadius: '12px',
								color: 'black',
								marginTop: '16px',
								width: '55%',
							}}
							iconMapping={{
								info: <InfoIcon color='disabled' />,
							}}
							variant='outlined'
							severity='info'
						>
							{sSystemID === OBJ_APP_HP.ID &&
								'Only use this system if your direct reports require access to the UPMC Health Plan Clinical Platform Epic instance.'}
						</Alert>
					)}
				</Box>
			)}
			{sArrReports.length < 1 && !rError && sHasUnmapped === false && (
				<Alert
					sx={{
						margin: '16px auto',
						width: '45%',
					}}
					severity={sSelectedReport || reportID ? 'warning' : 'info'}
				>
					{sSelectedReport || reportID
						? 'The specified direct report could not be found or does not report to you.'
						: 'You have no eligible direct reports and are not responsible for performing manager validations.'}
				</Alert>
			)}
			{sHasUnmapped && (
				<Alert
					sx={{
						alignItems: 'center',
						margin: '16px auto',
						width: '45%',
						'& .MuiAlert-action': {
							paddingLeft: '0',
						},
					}}
					severity='warning'
					onClose={() => setHasUnmapped(false)}
				>
					{sSelectedReport || reportID
						? 'The direct report '
						: 'Some of your reports '}
					could not be mapped to a job category and{' '}
					{sSelectedReport || reportID ? 'has ' : 'have '}a blank
					value for your review.
				</Alert>
			)}
			{sArrReports.length > 0 && !sSelectedReport && !reportID && (
				<ProgressBar
					id='progress-bar--completion'
					color={'success'}
					progress={completionProgress}
					slots={{
						bar: {
							sx: {
								width: `${(25 / 30) * 100}%`,
								margin: '12px auto',
							},
						},
						label: {
							sx: {
								margin: '12px 0 -12px 0',
							},
						},
					}}
					sx={{
						width: '30%',
						margin: '16px auto',
					}}
				/>
			)}
			{!sTrackingNumber && (
				<Box
					sx={{
						width:
							rNavDrawerOpen && sIsDrawerOpen
								? '62.5%'
								: sIsDrawerOpen
									? '67.5%'
									: rNavDrawerOpen
										? '72.5%'
										: '80%',
						marginLeft: `${24 + 56 + 20}px`,
						paddingTop:
							(sSelectedReport && !sHasUnmapped) || reportID
								? '36px'
								: 'unset',
						paddingBottom: '24px',
					}}
				>
					<ValidationTable
						loading={sIsLoading}
						reports={sArrReports}
						readOnly={sIsLoading || sIsRestricted}
						systemID={sSystemID}
						onCreateHandler={onCreateHandler}
						onDeleteHandler={onDeleteHandler}
						onEditHandler={
							sSelectedReport || reportID ? null : onEditHandler
						}
						onJobCategoryHandler={onJobCategoryHandler}
						onToggleHandler={onToggleHandler}
						onValidateHandler={onValidateHandler}
						onRowInfoClick={onRowInfoHandler}
					/>
				</Box>
			)}
			{(sSelectedReport || reportID) && !sTrackingNumber && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							marginBottom: '8px',
						}}
					>
						{sArrReports.length > 0 && (
							<>
								{sSelectedReport && (
									<Button
										sx={{ marginRight: '4px' }}
										variant='outlined'
										disabled={sIsLoading}
										onClick={onDiscardWarningHandler}
										startIcon={<ArrowBackIcon />}
									>
										Discard
									</Button>
								)}
								<Button
									sx={{ marginLeft: '4px' }}
									disabled={!enableSave}
									startIcon={
										sIsLoading ? (
											<CircularProgress size={24} />
										) : (
											<SaveIcon />
										)
									}
									onClick={onSubmitHandler}
								>
									Save
								</Button>
							</>
						)}
					</Box>
					{sSelectedReport && sArrReports.length < 1 && (
						<Button
							variant='outlined'
							onClick={onDiscardWarningHandler}
						>
							Go Back
						</Button>
					)}
				</Box>
			)}
			{sTrackingNumber && reportID && (
				<Card sx={{ width: '50%', margin: '0 auto' }}>
					<CardHeader
						sx={{
							'& .MuiCardHeader-title': {
								color: 'primary.main',
							},
						}}
						title='Confirmation'
					/>
					<CardContent sx={{ paddingTop: '0' }}>
						Request <b>{sTrackingNumber}</b> has been successfully
						initiated according to your validation.
					</CardContent>
					<CardActions sx={{ justifyContent: 'center' }}>
						<HomeButton icon />
					</CardActions>
				</Card>
			)}
			<Snackbar
				anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
				open={sSnackbarOpen}
				autoHideDuration={3000}
				onClose={() => {
					setSnackbarOpen(false);
				}}
			>
				<Alert
					onClose={() => {
						setSnackbarOpen(false);
					}}
					severity='success'
					variant='filled'
				>
					{`${
						sSystemID === OBJ_APP_BRIDGES.ID
							? 'Request'
							: 'Validation'
					}
						submitted!`}
				</Alert>
			</Snackbar>
			{(sArrReports.length > 0 ||
				rError ||
				reportID ||
				sSelectedReport) && (
				<>
					<SideDrawer open={sIsDrawerOpen} setOpen={setDrawerOpen}>
						<JobCategoryDirectory
							key={sJobCategoryDirectoryKey}
							selection={sClickedJobCategory}
							systemID={sSystemID}
						/>
					</SideDrawer>
					<SpeedDial
						classes={CLASS_SPEED_DIAL}
						sx={{
							position: 'fixed',
							bottom: 56 + 8 + 24,
							left: 24,
						}}
						ariaLabel='Help'
						icon={
							<SpeedDialIcon
								icon={
									<MoreVertIcon
										key={'fab--help'}
										fontSize='medium'
									/>
								}
								openIcon={<CloseIcon />}
							/>
						}
						onClick={() => setIsSpeedDialOpen(isOpen => !isOpen)}
						open={sIsSpeedDialOpen}
					>
						<SpeedDialAction
							classes={CLASS_SPEED_DIAL}
							key='0'
							icon={<QuestionMarkIcon color='secondary' />}
							tooltipTitle='FAQ'
							tooltipPlacement='right'
							onClick={() => window.open(LINK_FAQ, '_blank')}
						/>
						<SpeedDialAction
							classes={CLASS_SPEED_DIAL}
							key='1'
							icon={<SwitchAccountIcon color='secondary' />}
							tooltipTitle='Proxy'
							tooltipPlacement='right'
							onClick={() => {
								setIsProxyDialogOpen(true);
								onDialogOpenHandler();
							}}
						/>
					</SpeedDial>
					<ProxyDialog
						open={sIsProxyDialogOpen}
						options={sProxies}
						onChange={onProxyHandler}
						onClose={() => setIsProxyDialogOpen(false)}
						value={sSelectedProxy}
					/>
					<DiscardDialog
						open={sIsDiscardDialogOpen}
						onAccept={onDiscardHandler}
						onDecline={() => setIsDiscardDialogOpen(false)}
					/>
					{missingSupplement && (
						<Supplement
							id={missingSupplement.id}
							open
							onAccept={onSupplementAcceptHandler}
							onDiscard={onSupplementDiscardHandler}
							type={missingSupplement.type}
							lastName={sArrReports[0]?.name.split(',')[0]}
							firstName={sArrReports[0]?.name.split(' ')[1]}
						/>
					)}
				</>
			)}
		</ThemeProvider>
	);
};

export default ManagerValidation;
